import React from 'react';
import { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { getChartSeriesColor } from '../../../utils/chartUtils';
import { getAbbreviatedSubjectname, SUBJECT_ABBREVIATIONS } from '../../../utils/subjectUtils';

export const BarChartComponent = ({ chartData, theme, enableAnimation = true }) => {
  const { labels, datasets, options = {}, title } = chartData;

  const [transformedData, setTransformedData] = useState([]);

  useEffect(()=>{
    const _data = labels.map((label, index) => {
      const formattedLabel = getLabel(label);
      const dataPoint = { name: formattedLabel };
      datasets.forEach(dataset => {
        dataPoint[dataset.label] = dataset.data[index];
      });
      return dataPoint;
    });
    setTransformedData(_data);
  },[labels, datasets]);
  // Generate colors for different datasets
  const getBarColor = (index) => getChartSeriesColor(index, theme.isDark);

  const getLabel = (label) => {
    for (const key of Object.keys(SUBJECT_ABBREVIATIONS)) {
      if (label.includes(key)) {
        return getAbbreviatedSubjectname(key);
      }
    }
    return label;
  }

  // Transform data for recharts format
  // const transformedData = labels.map((label, index) => {
  //   const formattedLabel = getLabel(label);
  //   const dataPoint = { name: formattedLabel };
  //   datasets.forEach(dataset => {
  //     dataPoint[dataset.label] = dataset.data[index];
  //   });
  //   return dataPoint;
  // });

  return (
    <div className="w-full">
      {title && (
        <h3 
          className="mb-3 text-[15px] font-semibold"
          style={{ color: theme.colors.text }}
        >
          {title}
        </h3>
      )}
      
      <div 
        className="w-full"
        style={{ 
          height: options.chartHeight || '500px', // height of the bar chart
          width: options.chartWidth || '100%' // width of the bar chart
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={transformedData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: datasets.length > 1 ? 80 : 60,
            }}
          >
            <CartesianGrid 
              strokeDasharray="3 3"
              stroke={theme.colors.cardBorder || theme.colors.border}
              opacity={0.3}
            />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: 12,
                fill: theme.colors.textSecondary || theme.colors.text
              }}
              label={{
                value: options.xAxisTitle || '',
                position: 'insideBottom',
                offset: datasets.length > 1 ? -35 : -10,
                style: {
                  textAnchor: 'middle',
                  fill: theme.colors.text,
                  fontSize: '12px'
                }
              }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: 12,
                fill: theme.colors.textSecondary || theme.colors.text
              }}
              label={{
                value: options.yAxisTitle || '',
                angle: -90,
                position: 'insideLeft',
                style: {
                  textAnchor: 'middle',
                  fill: theme.colors.text,
                  fontSize: '12px'
                }
              }}
              // Manual y-axis interval configuration
              // interval={options.yAxisInterval !== undefined ? options.yAxisInterval : 'preserveEnd'}
              ticks={options.yAxisTicks || [0, 5, 10,15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100]}
            />
            <Tooltip
              cursorStyle={{
                cursor: 'help'
              }}
              contentStyle={{
                backgroundColor: theme.colors.surfaceElevated || theme.colors.cardBackground,
                border: `1px solid ${theme.colors.cardBorder}`,
                borderRadius: '6px',
                color: theme.colors.text,
                fontSize: '12px'
              }}
              labelStyle={{
                color: theme.colors.text,
                fontWeight: '600'
              }}
            />
            {datasets.length > 1 && (
              <Legend
                wrapperStyle={{
                  color: theme.colors.text,
                  fontSize: '12px'
                }}
              />
            )}
            {datasets.map((dataset, index) => (
              <Bar
                key={dataset.label}
                dataKey={dataset.label}
                fill={getBarColor(index)}
                radius={[6, 6, 0, 0]}
                name={dataset.label}
                isAnimationActive={enableAnimation}
                animationDuration={enableAnimation ? 400 : 0}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Keep the old function for backward compatibility
export const renderBarChart = (chartData, theme, enableAnimation = true) => {
  return (
    <BarChartComponent 
      chartData={chartData} 
      theme={theme} 
      enableAnimation={enableAnimation} 
    />
  );
};
